import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  EscoSearchResult,
  JobPostRequiredSkill
} from 'core/interface/escoSkill';
import { SkillService } from 'services/skills/skillService';
import { useDebounce } from 'helpers/useDebounce';
import useLocales from 'hooks/useLocales';
import Image from 'components/image/Image';
import {
  jobPostSkillDisplayLabel,
  jobPostSkillIdentity,
  jobPostSkillMatchKeys,
  isEscoSearchResultSelected,
  isJobPostSkillSelected
} from 'utils/jobPostSkills';

import './Index.scss';

const SUGGESTION_LIMIT = 12;

type EscoSkillsPickerProps = {
  value: JobPostRequiredSkill[];
  onChange: (skills: JobPostRequiredSkill[]) => void;
  jobTitle?: string;
  error?: boolean;
  readOnly?: boolean;
  initialSavedSkills?: JobPostRequiredSkill[];
};

const EscoSkillsPicker: FC<EscoSkillsPickerProps> = ({
  value,
  onChange,
  jobTitle = '',
  error = false,
  readOnly = false,
  initialSavedSkills = []
}) => {
  const { t, currentLang } = useLocales();
  const lang = currentLang?.value || 'en';

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<EscoSearchResult[]>([]);
  const [titleSuggestions, setTitleSuggestions] = useState<EscoSearchResult[]>(
    []
  );
  const [isManualSearchLoading, setIsManualSearchLoading] = useState(false);
  const [isTitleSearchLoading, setIsTitleSearchLoading] = useState(false);
  const [resolvedManualQuery, setResolvedManualQuery] = useState('');
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [showAllSelected, setShowAllSelected] = useState(false);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const debouncedJobTitle = useDebounce(jobTitle.trim(), 1000);

  const manualSearchSeqRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedMatchKeys = useMemo(() => {
    const keys = new Set<string>();
    value.forEach((skill) => {
      jobPostSkillMatchKeys(skill, lang).forEach((key) => keys.add(key));
    });
    return keys;
  }, [value, lang]);
  const savedIdentitySet = useMemo(
    () =>
      new Set(initialSavedSkills.map((skill) => jobPostSkillIdentity(skill))),
    [initialSavedSkills]
  );

  const visibleSuggestions = showAllSuggestions
    ? titleSuggestions
    : titleSuggestions.slice(0, SUGGESTION_LIMIT);
  const visibleSelected = showAllSelected
    ? value
    : value.slice(0, SUGGESTION_LIMIT);

  const addSkill = useCallback(
    (skill: JobPostRequiredSkill) => {
      if (isJobPostSkillSelected(skill, selectedMatchKeys, lang)) return;
      onChange([...value, skill]);
    },
    [lang, onChange, selectedMatchKeys, value]
  );

  const removeSkill = useCallback(
    (skill: JobPostRequiredSkill) => {
      const identity = jobPostSkillIdentity(skill);
      onChange(
        value.filter((item) => jobPostSkillIdentity(item) !== identity)
      );
    },
    [onChange, value]
  );

  const addEscoResult = useCallback(
    (result: EscoSearchResult) => {
      addSkill({
        title: result.title,
        name: result.name,
        uri: result.uri
      });
      setSearchQuery('');
      setSearchResults([]);
      setResolvedManualQuery('');
    },
    [addSkill]
  );

  const addCustomSkill = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      addSkill({ name: trimmed });
      setSearchQuery('');
      setSearchResults([]);
      setResolvedManualQuery('');
    },
    [addSkill]
  );

  useEffect(() => {
    if (!debouncedJobTitle) {
      setTitleSuggestions([]);
      setIsTitleSearchLoading(false);
      return;
    }

    let cancelled = false;
    setIsTitleSearchLoading(true);

    SkillService.getEscoSearch({
      lang,
      type: 'skill',
      q: debouncedJobTitle
    })
      .then((results) => {
        if (!cancelled) {
          setTitleSuggestions(results);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsTitleSearchLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedJobTitle, lang]);

  useEffect(() => {
    const trimmed = debouncedSearchQuery.trim();
    if (!trimmed) {
      setSearchResults([]);
      setResolvedManualQuery('');
      setIsManualSearchLoading(false);
      return;
    }

    const seq = ++manualSearchSeqRef.current;
    setIsManualSearchLoading(true);

    SkillService.getEscoSearch({
      lang,
      type: 'skill',
      q: trimmed
    })
      .then((results) => {
        if (seq !== manualSearchSeqRef.current) return;
        setSearchResults(results);
        setResolvedManualQuery(trimmed);
      })
      .finally(() => {
        if (seq === manualSearchSeqRef.current) {
          setIsManualSearchLoading(false);
        }
      });
  }, [debouncedSearchQuery, lang]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setSearchQuery('');
        setSearchResults([]);
        setResolvedManualQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const trimmedSearch = searchQuery.trim();
  const showCustomSkillOption =
    !readOnly &&
    Boolean(trimmedSearch) &&
    !isManualSearchLoading &&
    resolvedManualQuery.toLowerCase() === trimmedSearch.toLowerCase() &&
    !searchResults.some(
      (result) =>
        result.title.trim().toLowerCase() === trimmedSearch.toLowerCase()
    );

  const renderSelectedChip = (skill: JobPostRequiredSkill) => {
    const identity = jobPostSkillIdentity(skill);
    const isSaved = savedIdentitySet.has(identity);
    const label = jobPostSkillDisplayLabel(skill, lang);

    return (
      <span
        className={`esco-skills-picker__selected-chip ${
          isSaved ? 'esco-skills-picker__selected-chip--saved' : ''
        }`}
        key={identity}
      >
        {label}
        {!readOnly && (
          <button
            type="button"
            className="esco-skills-picker__remove-btn"
            onClick={() => removeSkill(skill)}
            aria-label={t('Remove skill')}
          >
            <Image className="remove" name="removes.svg" width={10} height={10} />
          </button>
        )}
      </span>
    );
  };

  return (
    <div
      className={`esco-skills-picker ${error ? 'esco-skills-picker--error' : ''}`}
      ref={containerRef}
    >
      <div className="esco-skills-picker__section">
        <span className="esco-skills-picker__section-title">
          {t('Required skills')}
        </span>
        {value.length > 0 ? (
          <>
            <div className="esco-skills-picker__selected-list">
              {visibleSelected.map(renderSelectedChip)}
            </div>
            {value.length > SUGGESTION_LIMIT && (
              <Button
                type="button"
                size="small"
                className="esco-skills-picker__toggle-btn"
                onClick={() => setShowAllSelected((prev) => !prev)}
              >
                {showAllSelected ? t('Show less') : t('Show more')}
              </Button>
            )}
          </>
        ) : (
          <span className="esco-skills-picker__empty">
            {readOnly
              ? t('No skills selected')
              : t('Add skills using suggestions or search below')}
          </span>
        )}
      </div>

      {!readOnly && (
        <>
          <div className="esco-skills-picker__section">
            <button
              type="button"
              className="esco-skills-picker__section-toggle"
              onClick={() => setSuggestionsExpanded((prev) => !prev)}
              aria-expanded={suggestionsExpanded}
            >
              <span className="esco-skills-picker__section-title">
                {t('Suggested skills')}
                {titleSuggestions.length > 0 && (
                  <span className="esco-skills-picker__section-count">
                    ({titleSuggestions.length})
                  </span>
                )}
              </span>
              {suggestionsExpanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </button>
            {suggestionsExpanded && (
              <>
                {!jobTitle.trim() ? (
                  <span className="esco-skills-picker__hint">
                    {t('Enter a job title to see suggested skills')}
                  </span>
                ) : isTitleSearchLoading ? (
                  <span className="esco-skills-picker__hint">
                    <CircularProgress size={14} /> {t('Loading suggestions...')}
                  </span>
                ) : titleSuggestions.length === 0 ? (
                  <span className="esco-skills-picker__hint">
                    {t('No suggestions found. Try searching for skills below.')}
                  </span>
                ) : (
                  <>
                    <div className="esco-skills-picker__suggestions">
                      {visibleSuggestions.map((result) => {
                        const isSelected = isEscoSearchResultSelected(
                          result,
                          selectedMatchKeys
                        );
                        return (
                          <Chip
                            key={result.uri}
                            label={result.title}
                            icon={<AddIcon />}
                            className="esco-skills-picker__suggestion-chip"
                            disabled={isSelected}
                            onClick={() => {
                              if (!isSelected) addEscoResult(result);
                            }}
                          />
                        );
                      })}
                    </div>
                    {titleSuggestions.length > SUGGESTION_LIMIT && (
                      <Button
                        type="button"
                        size="small"
                        className="esco-skills-picker__toggle-btn"
                        onClick={() => setShowAllSuggestions((prev) => !prev)}
                      >
                        {showAllSuggestions ? t('Show less') : t('Show more')}
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="esco-skills-picker__section esco-skills-picker__search">
            <span className="esco-skills-picker__section-title" style={{ marginBottom: '5px' }}>
              {t('Search skills')}
            </span>
            <TextField
              fullWidth
              size="small"
              value={searchQuery}
              placeholder={t('Search skills...')}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="esco-skills-picker__search-input"
            />
            {(trimmedSearch || searchResults.length > 0) && (
              <div className="esco-skills-picker__results-panel">
                {isManualSearchLoading ? (
                  <span className="esco-skills-picker__hint">
                    <CircularProgress size={14} /> {t('Searching...')}
                  </span>
                ) : (
                  <>
                    {searchResults.length > 0 && (
                      <div className="esco-skills-picker__results">
                        {searchResults.map((result) => {
                          const isSelected = isEscoSearchResultSelected(
                            result,
                            selectedMatchKeys
                          );
                          return (
                            <Chip
                              key={result.uri}
                              label={result.title}
                              icon={<AddIcon />}
                              className="esco-skills-picker__result-chip"
                              disabled={isSelected}
                              onClick={() => {
                                if (!isSelected) addEscoResult(result);
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                    {showCustomSkillOption &&
                      !selectedMatchKeys.has(
                        `title:${trimmedSearch.toLowerCase()}`
                      ) &&
                      !selectedMatchKeys.has(
                        `custom:${trimmedSearch.toLowerCase()}`
                      ) && (
                      <button
                        type="button"
                        className="esco-skills-picker__custom-btn"
                        onClick={() => addCustomSkill(trimmedSearch)}
                      >
                        {t('Add as custom skill')}: &quot;{trimmedSearch}&quot;
                      </button>
                    )}
                    {!isManualSearchLoading &&
                      searchResults.length === 0 &&
                      !showCustomSkillOption && (
                        <span className="esco-skills-picker__hint">
                          {t('No skills found')}
                        </span>
                      )}
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EscoSkillsPicker;
